import React, { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { Plus, Trash2 } from 'lucide-react';
import type { BusinessHourDto, DayOfWeek } from '@kardume/types';
import { BUSINESS_HOUR_MAX_WINDOWS_PER_DAY } from '@kardume/types';
import ActionButton from './ActionButton';
import Button from './Button';
import Switch from './Switch';
import TextInput from './TextInput';

/** Week days in display order, with their Portuguese labels. */
export const WEEK_DAYS: { key: DayOfWeek; label: string }[] = [
  { key: 'MONDAY', label: 'Segunda' },
  { key: 'TUESDAY', label: 'Terça' },
  { key: 'WEDNESDAY', label: 'Quarta' },
  { key: 'THURSDAY', label: 'Quinta' },
  { key: 'FRIDAY', label: 'Sexta' },
  { key: 'SATURDAY', label: 'Sábado' },
  { key: 'SUNDAY', label: 'Domingo' },
];

/** One opening window of a day. Times are "HH:mm" (empty while being typed). */
export interface DayWindow {
  opensAt: string;
  closesAt: string;
}

/**
 * Editable opening hours of a single day.
 *
 * A day holds a list of windows because the store may close in between - the
 * classic case is the lunch break (08:00–12:00 and 13:30–19:00).
 */
export interface DayHours {
  closed: boolean;
  windows: DayWindow[];
}

/** The whole week, keyed by day. */
export type WeekHours = Record<DayOfWeek, DayHours>;

/** An empty row, for the merchant to fill in. */
export const emptyDayWindow = (): DayWindow => ({ opensAt: '', closesAt: '' });

const singleWindow = (opensAt: string, closesAt: string): DayHours => ({
  closed: false,
  windows: [{ opensAt, closesAt }],
});

/** Sensible defaults for a new store (weekdays 09:00-18:00, Sunday closed). */
export const defaultWeekHours = (): WeekHours => ({
  MONDAY: singleWindow('09:00', '18:00'),
  TUESDAY: singleWindow('09:00', '18:00'),
  WEDNESDAY: singleWindow('09:00', '18:00'),
  THURSDAY: singleWindow('09:00', '18:00'),
  FRIDAY: singleWindow('09:00', '18:00'),
  SATURDAY: singleWindow('09:00', '13:00'),
  SUNDAY: { closed: true, windows: [] },
});

/** "HH:mm" as minutes from midnight, or `null` when the field is not filled yet. */
const toMinutes = (time: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

const DAY_MINUTES = 24 * 60;

/**
 * True when the window crosses midnight - the closing time falls before the
 * opening one. That is the bar that opens at 18:30 and shuts at 01:00; the core
 * accepts it and splits it at midnight when storing (card 295).
 */
const crossesMidnight = (opensAt: number, closesAt: number): boolean => closesAt < opensAt;

/**
 * End of the window in minutes, counting an overnight shift as "tomorrow":
 * 01:00 becomes 25:00. Without it, "18:00-01:00" and "22:00-23:00" on the same
 * day would not look like the clash they are.
 */
const effectiveEnd = (opensAt: number, closesAt: number): number =>
  crossesMidnight(opensAt, closesAt) ? closesAt + DAY_MINUTES : closesAt;

/** True when the typed window runs past midnight - used for the "vira a noite" tag. */
export const isOvernight = (window: DayWindow): boolean => {
  const opensAt = toMinutes(window.opensAt);
  const closesAt = toMinutes(window.closesAt);
  if (opensAt === null || closesAt === null) return false;
  return crossesMidnight(opensAt, closesAt);
};

/**
 * Windows ordered by opening time, which is the order the store page reads.
 * A window still being typed has no opening time and stays where it is, at the
 * end - reordering a half-filled row under the merchant's cursor is jarring.
 */
const sortWindows = (windows: DayWindow[]): DayWindow[] =>
  [...windows].sort((a, b) => (toMinutes(a.opensAt) ?? Infinity) - (toMinutes(b.opensAt) ?? Infinity));

/**
 * Rebuild the editable week from the API representation.
 *
 * The payload is a flat list and the same `dayOfWeek` may appear more than once
 * - one item per window - so the day is rebuilt from every item that mentions
 * it. Days the API does not mention keep the default.
 */
export const weekHoursFrom = (hours?: BusinessHourDto[] | null): WeekHours => {
  const week = defaultWeekHours();
  const touched = new Set<DayOfWeek>();

  (hours ?? []).forEach((hour) => {
    if (!touched.has(hour.dayOfWeek)) {
      touched.add(hour.dayOfWeek);
      week[hour.dayOfWeek] = { closed: hour.closed, windows: [] };
    }
    if (hour.closed) return;
    const day = week[hour.dayOfWeek];
    day.closed = false;
    day.windows.push({ opensAt: hour.opensAt ?? '', closesAt: hour.closesAt ?? '' });
  });

  WEEK_DAYS.forEach(({ key }) => {
    const day = week[key];
    // An open day always shows at least one row, even when the core has no
    // window for it - otherwise the merchant would face a day with no fields.
    week[key] = {
      closed: day.closed,
      windows: day.closed ? day.windows : sortWindows(day.windows.length > 0 ? day.windows : [emptyDayWindow()]),
    };
  });

  return week;
};

/**
 * Convert the editable week into the API payload: one item per window, ordered
 * by opening time. A closed day carries no time at all, and an open day with no
 * usable window still goes as a single empty item so the core answers with
 * BUSINESS_HOUR_INCOMPLETE instead of quietly storing nothing.
 */
export const weekHoursToPayload = (week: WeekHours): BusinessHourDto[] =>
  WEEK_DAYS.reduce<BusinessHourDto[]>((payload, { key }) => {
    const day = week[key];
    if (day.closed) {
      payload.push({ dayOfWeek: key, closed: true });
      return payload;
    }
    const windows = sortWindows(day.windows).filter((window) => !!window.opensAt || !!window.closesAt);
    if (windows.length === 0) {
      payload.push({ dayOfWeek: key, closed: false });
      return payload;
    }
    windows.forEach((window) => {
      payload.push({
        dayOfWeek: key,
        closed: false,
        opensAt: window.opensAt || undefined,
        closesAt: window.closesAt || undefined,
      });
    });
    return payload;
  }, []);

/** Validation messages for one window, keyed by the field they belong to. */
export interface DayWindowErrors {
  opensAt?: string;
  closesAt?: string;
}

/** What is wrong with a single day: the day as a whole and each of its windows. */
export interface DayHoursErrors {
  /** Problem with the day itself (today only the window limit). */
  day?: string;
  /** One entry per window, aligned by index with `DayHours.windows`. */
  windows: DayWindowErrors[];
}

export type WeekHoursErrors = Record<DayOfWeek, DayHoursErrors>;

/**
 * Mirrors the core's validation so the merchant sees the problem before the
 * request, on the very window that causes it.
 *
 * A closed day is never checked: its windows are kept in state only so that
 * reopening the day brings the times back, and `weekHoursToPayload` drops them.
 * That is also why BUSINESS_HOUR_CLOSED_WITH_WINDOW cannot happen from here.
 */
export const validateWeekHours = (week: WeekHours): WeekHoursErrors => {
  const errors = {} as WeekHoursErrors;

  WEEK_DAYS.forEach(({ key }) => {
    const day = week[key];
    const dayErrors: DayHoursErrors = { windows: day.windows.map(() => ({})) };
    errors[key] = dayErrors;
    if (day.closed) return;

    if (day.windows.length > BUSINESS_HOUR_MAX_WINDOWS_PER_DAY) {
      dayErrors.day = `Cadastre no máximo ${BUSINESS_HOUR_MAX_WINDOWS_PER_DAY} horários por dia.`;
    }

    const ranges = day.windows.map((window) => ({
      opensAt: toMinutes(window.opensAt),
      closesAt: toMinutes(window.closesAt),
    }));

    day.windows.forEach((_, index) => {
      const windowErrors = dayErrors.windows[index];
      const { opensAt, closesAt } = ranges[index];

      if (opensAt === null) windowErrors.opensAt = 'Informe a abertura.';
      if (closesAt === null) windowErrors.closesAt = 'Informe o fechamento.';
      if (opensAt === null || closesAt === null) return;

      // Closing before opening means the shift crosses midnight, and that is
      // now accepted: the core splits it at midnight on write and joins it back
      // on read, so the merchant never does that arithmetic (card 295). What is
      // still nonsense is closing on the very minute it opens - a window of
      // either zero or twenty-four hours, and there is no telling which.
      if (closesAt === opensAt) {
        windowErrors.closesAt = 'O fechamento não pode ser igual à abertura.';
        return;
      }

      const end = effectiveEnd(opensAt, closesAt);

      // Touching windows (12:00–13:00 then 13:00–18:00) are the same shift typed
      // on two rows and are allowed; anything that shares a minute is not, and
      // that includes a window fully inside another. The message lands on the
      // window that starts later, which is the one the merchant has to move.
      const overlapsEarlier = ranges.some((other, otherIndex) => {
        if (otherIndex === index || other.opensAt === null || other.closesAt === null) return false;
        if (other.closesAt === other.opensAt) return false;
        const otherEnd = effectiveEnd(other.opensAt, other.closesAt);
        const isEarlier = other.opensAt < opensAt || (other.opensAt === opensAt && otherIndex < index);
        return isEarlier && other.opensAt < end && opensAt < otherEnd;
      });
      if (overlapsEarlier) windowErrors.opensAt = 'Este horário se sobrepõe a outro do mesmo dia.';
    });
  });

  // The overnight tail lands on the next day and may fall on top of its own
  // opening - Friday 18:30–03:00 with Saturday 02:00–12:00. The core refuses it;
  // saying so here saves the round trip and points at the window to move.
  WEEK_DAYS.forEach(({ key }, index) => {
    const day = week[key];
    if (day.closed) return;
    const nextKey = WEEK_DAYS[(index + 1) % WEEK_DAYS.length].key;
    const next = week[nextKey];
    if (next.closed) return;

    const tailEnd = day.windows.reduce<number>((furthest, window) => {
      const opensAt = toMinutes(window.opensAt);
      const closesAt = toMinutes(window.closesAt);
      if (opensAt === null || closesAt === null || !crossesMidnight(opensAt, closesAt)) return furthest;
      return Math.max(furthest, closesAt);
    }, 0);
    if (tailEnd === 0) return;

    next.windows.forEach((window, windowIndex) => {
      const opensAt = toMinutes(window.opensAt);
      if (opensAt === null || opensAt >= tailEnd) return;
      const windowErrors = errors[nextKey].windows[windowIndex];
      if (!windowErrors.opensAt) {
        windowErrors.opensAt = 'Este horário se sobrepõe ao turno que vem da noite anterior.';
      }
    });
  });

  return errors;
};

/** True when any day still has something to fix. */
export const hasWeekHoursErrors = (errors: WeekHoursErrors): boolean =>
  WEEK_DAYS.some(
    ({ key }) =>
      !!errors[key].day || errors[key].windows.some((window) => !!window.opensAt || !!window.closesAt)
  );

/**
 * True when the week can be sent. Kept under the old name because it is what
 * the screens gate their "next step" on, but it now covers every rule the core
 * applies - not only the missing times it checked when a day had a single window.
 */
export const isWeekHoursComplete = (week: WeekHours): boolean => !hasWeekHoursErrors(validateWeekHours(week));

const Card = styled.div`
  background: ${(p) => p.theme.surfaceAlt};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 96px 120px 1fr;
  align-items: start;
  gap: 0.75rem;
  padding: 0.6rem 0;

  & + & {
    border-top: 1px solid ${(p) => p.theme.border};
  }

  .day {
    font-size: 0.85rem;
    font-weight: 600;
    color: ${(p) => p.theme.textPrimary};
    /* Keeps the label level with the first 42px field. */
    line-height: 42px;
  }
  .toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-height: 42px;
    font-size: 0.78rem;
    color: ${(p) => p.theme.textMuted};
  }
  .closed {
    min-height: 42px;
    display: flex;
    align-items: center;
    font-size: 0.82rem;
    color: ${(p) => p.theme.textMuted};
    font-style: italic;
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
    gap: 0.4rem;

    .day {
      line-height: 1.4;
    }
  }
`;

const Windows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

const WindowRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr auto;
  align-items: start;
  gap: 0.5rem;

  .separator {
    /* Sits on the field's line, not on the error text below it. */
    line-height: 42px;
    font-size: 0.8rem;
    color: ${(p) => p.theme.textMuted};
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr auto 1fr;

    .actions {
      grid-column: 1 / span 3;
      justify-content: flex-end;
    }
  }
`;

const WindowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-height: 42px;
`;

const DayFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const AddButton = styled(Button)`
  min-height: 34px;
  padding: 0.35rem 0.7rem;
  font-size: 0.78rem;
`;

const DayError = styled.span`
  font-size: 0.75rem;
  color: ${(p) => p.theme.error};
`;

/**
 * Confirms out loud that the merchant meant "until 01:00 tomorrow", not a typo.
 * Without it, a closing time smaller than the opening one reads like a mistake
 * the screen silently accepted.
 */
const OvernightTag = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
  padding: 0.15rem 0.4rem;
  border-radius: ${(p) => p.theme.radius};
  color: ${(p) => p.theme.accent};
  background: ${(p) => p.theme.accent}1a;
`;

export interface BusinessHoursInputProps {
  value: WeekHours;
  /** Emits a partial patch for a single day. */
  onChange: (day: DayOfWeek, patch: Partial<DayHours>) => void;
  disabled?: boolean;
  /** Windows allowed per day; mirrors the core's own limit. */
  maxWindowsPerDay?: number;
}

/**
 * Weekly opening-hours grid shared by the backoffice merchant form, the
 * self-service onboarding wizard and the merchant panel's own "Horários" screen:
 * one row per day with an open/closed switch and the list of "HH:mm" windows the
 * store is open on that day.
 *
 * Validation is computed here so each window shows its own message while the
 * merchant types; `validateWeekHours` is exported so the screen that owns the
 * submit button can gate on the very same rules instead of writing a second set.
 *
 * A shift that runs past midnight is typed as one window ("18:30" to "01:00")
 * and tagged as such. Splitting it into two rows is the core's job on write, and
 * joining it back is the core's job on read (`BusinessHourWindows`, card 295) -
 * the merchant must never be the one doing that arithmetic. Note that only the
 * merchant panel's endpoint joins the shift back today: the backoffice merchant
 * form still reads the two stored rows.
 */
export const BusinessHoursInput: React.FC<BusinessHoursInputProps> = ({
  value,
  onChange,
  disabled,
  maxWindowsPerDay = BUSINESS_HOUR_MAX_WINDOWS_PER_DAY,
}) => {
  const theme = useTheme();
  const errors = useMemo(() => validateWeekHours(value), [value]);

  const patchWindow = (day: DayOfWeek, index: number, changes: Partial<DayWindow>): void =>
    onChange(day, {
      windows: value[day].windows.map((window, i) => (i === index ? { ...window, ...changes } : window)),
    });

  const addWindow = (day: DayOfWeek): void =>
    onChange(day, { windows: [...value[day].windows, emptyDayWindow()] });

  const removeWindow = (day: DayOfWeek, index: number): void =>
    onChange(day, { windows: value[day].windows.filter((_, i) => i !== index) });

  /**
   * Closing a day keeps its windows in state on purpose: the merchant who
   * toggles a day by mistake gets the times back instead of retyping them, and
   * the payload drops them anyway.
   */
  const setClosed = (day: DayOfWeek, closed: boolean): void =>
    onChange(day, {
      closed,
      windows: !closed && value[day].windows.length === 0 ? [emptyDayWindow()] : value[day].windows,
    });

  return (
    <Card>
      {WEEK_DAYS.map(({ key, label }) => {
        const hours = value[key];
        const dayErrors = errors[key];
        const canAdd = !disabled && !hours.closed && hours.windows.length < maxWindowsPerDay;

        return (
          <Row key={key}>
            <span className="day">{label}</span>
            <label className="toggle">
              <Switch
                checked={!hours.closed}
                disabled={disabled}
                onChange={(open: boolean) => setClosed(key, !open)}
              />
              {hours.closed ? 'Fechado' : 'Aberto'}
            </label>

            {hours.closed ? (
              <span className="closed">Fechado o dia todo</span>
            ) : (
              <Windows>
                {hours.windows.map((window, index) => (
                  <WindowRow key={index}>
                    <TextInput
                      type="time"
                      aria-label={`${label} - abre às (horário ${index + 1})`}
                      disabled={disabled}
                      value={window.opensAt}
                      error={dayErrors.windows[index]?.opensAt}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        patchWindow(key, index, { opensAt: e.target.value })
                      }
                    />
                    <span className="separator">às</span>
                    <TextInput
                      type="time"
                      aria-label={`${label} - fecha às (horário ${index + 1})`}
                      disabled={disabled}
                      value={window.closesAt}
                      error={dayErrors.windows[index]?.closesAt}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        patchWindow(key, index, { closesAt: e.target.value })
                      }
                    />
                    <WindowActions className="actions">
                      {isOvernight(window) && <OvernightTag title="Este horário termina no dia seguinte">vira a noite</OvernightTag>}
                      <ActionButton
                        type="button"
                        title={
                          hours.windows.length === 1
                            ? 'Para não abrir neste dia, desligue o dia'
                            : 'Remover horário'
                        }
                        aria-label={`Remover o horário ${index + 1} de ${label}`}
                        color={theme.error}
                        // The last window is not removable: a day with no window
                        // is not "closed", it is an incomplete day. Closing is
                        // what the switch is for.
                        disabled={disabled || hours.windows.length === 1}
                        onClick={() => removeWindow(key, index)}
                      >
                        <Trash2 size={15} />
                      </ActionButton>
                    </WindowActions>
                  </WindowRow>
                ))}

                <DayFooter>
                  <AddButton
                    type="button"
                    variant="ghost"
                    disabled={!canAdd}
                    title={
                      hours.windows.length >= maxWindowsPerDay
                        ? `No máximo ${maxWindowsPerDay} horários por dia`
                        : undefined
                    }
                    onClick={() => addWindow(key)}
                  >
                    <Plus size={14} /> Adicionar horário
                  </AddButton>
                  {dayErrors.day && <DayError>{dayErrors.day}</DayError>}
                </DayFooter>
              </Windows>
            )}
          </Row>
        );
      })}
    </Card>
  );
};

export default BusinessHoursInput;
